import React, { useEffect, useState, useMemo, useRef } from 'react';
import { css } from '@emotion/css';
import ContentHitModel from '../../../models/analytics/api/ContentHitModel';
import TableCell from '../../../components/general/TableCell';
import { color_text_default, color_text_link, color_colors_ocean, ocean_blue, color_text_light, color_gradients_green_shift } from '../../../constants/colors';
import moment from 'moment';
import FeatureContainer from '../../../state/containers/FeatureContainer';
import TopContentHitModel from '../../../models/analytics/api/TopContentHitModel';
import ReportTableHeader from '../../../components/analytics/ReportTableHeader';
import ReportTable from '../../../components/analytics/ReportTable';
import { getEditUrl, getSmallFeatureTypeIcon } from '../../../models/extensions';
import StatisticsModel from '../../../models/analytics/api/StatisticsModel';
import { DATE_FORMAT } from '../../../models/analytics/AnalyticsDateFormat';
import TotalCard from '../../../components/charts/TotalCard';
import { FallbackFeatureTypeId, SPARK_EXCLUDED_FEATURE_TYPE_IDS } from '../../../constants/featureTypeIds';
import styled from '@emotion/styled';
import SparkContainer from '../../../hooks/SparkContainer';
import SessionAnalyticsContainer from '../../../hooks/SessionAnalyticsContainer';
import TelephonyAnalyticsContainer from '../../../hooks/TelephonyAnalyticsContainer';
import Loader from '../../../components/general/Loader';
import ApplicationStatisticsContainer from '../../../state/containers/ApplicationStatisticsContainer';
import { ContentHitLoadingState } from './ApplicationStatistics';
import GenerativeReportTable from '../../../components/analytics/GenerativeReportTable';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';
import { getBaseUrl, getSparkBaseUrl } from '../../../constants/Urls';
import useIsMobile from '../../../hooks/UseIsMobile';
import { FoodOrderingFeatureFlagId, GENERATIVE_CALL_REVIEW_FEATURE_FLAG_IDS, HealthcareSchedulingFeatureFlagId, OutboundCallFeatureFlagId } from '../../../constants/featureFlagIds';
import { TAGS, getCategoryName, ALL_CATEGORIES } from '../../../constants/analyticsConstants/generativeCallReview';
import { useTagConfig } from '../../../hooks/TagConfigContainer';
import { getApplicationEngagements, OrganizationEngagement, getTopLevelMetrics, EngagementAnalyticsFilter, TopLevelMetrics } from '../../../api/controllers/engagementAnalyticsReporting';
import { mapEngagementsToSessionReviews, computeEngagementStats, loadEngagementsPaged, buildTimezoneAwareDateFilter, parseAnalyticsDate } from '../../../utils/engagementAnalyticsTransformers';
import { GraphFilterModal } from '../../../components/analytics/GraphFilterModal';
import Button from '../../../components/general/Button';
import SmallModalLayout from '../../../components/structure/SmallModalLayout';
import ModalHeader from '../../../components/general/ModalHeader';
import HistogramContainer from '../../../components/charts/ConvSuccessHistogramContainer';
import HistogramChartCard from '../../../components/charts/HistogramChartCard';
import { getAppointmentSummary } from '../../../api/controllers/medicalIntegrationConfiguration';
import TooltipWrapper from '../../../components/general/TooltipWrapper';
// OutboundToggleButton and OutboundMetricsGrid no longer used - replaced with inline toggle and TotalCards

// Helper to format hours as "Xh Ym" for Staff/Labor Hours Saved display
// Matches format used in DentalSchedulingDashboard
const formatHoursSaved = (hours: number): string => {
    if (hours === undefined || hours === null) return '0h 0m';
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (wholeHours === 0) {
        return `${minutes}m`;
    }
    return `${wholeHours}h ${minutes}m`;
};

const phoneIconBlue = require('../../../content/images/spark/phone.svg');
const averageIconBlue = require('../../../content/images/spark/average.svg');
const questionIconBlue = require('../../../content/images/spark/question.svg');
const laborHoursSavedIcon = require('../../../content/images/spark/minutes-blue.svg');
const uniqueCallersImage = require('../../../content/images/spark/phone-action-add-user.svg');
const callLengthImage = require('../../../content/images/spark/phone-actions-clock.svg');
const orderPlacedIconBlue = require('../../../content/images/spark/orderPlacedBlue.svg');
const appointmentUpdatesIcon = require('../../../content/images/analytics-icons/appointment-updates.svg');
const convertedCustomersIcon = require('../../../content/images/ordering/converted-customers.svg');
const upsellPercentageIcon = require('../../../content/images/ordering/upsell-percentage-2.svg');
const repeatCustomersIcon = require('../../../content/images/ordering/repeat-customers-2.svg');
const containedCallsIcon = require('../../../content/images/spark/lock-shield.svg');

interface ApplicationTopHitsTableProps {
    data: TopContentHitModel
    totals: StatisticsModel
    previousTotals: StatisticsModel
    featureContainer: FeatureContainer
    isLoading: ContentHitLoadingState
    startDate: string
    endDate: string
    showValue: boolean
    showPercentage: boolean
    statisticsContainer: ApplicationStatisticsContainer
    filteredInteractionsCount: number
    aggregatedFeatureTypeInteractionsLoaded: boolean
    previousDates: { startDate: string, endDate: string }
    previousAggregatedFeatureTypeInteractionsLoaded: boolean
    previousInteractionsCount: number
    applicationId: string
    application: ApplicationModel
    organizationId?: string
}

type StyledTotalCardTitle = "Engagements" | "Orders Placed" | "Appointment Updates" | "Repeat Customers" | "Converted Customers" | "Upsell Percentage" | "Labor Hours Saved" | "Staff Hours Saved" | "Average Call Length" | "Call Containment" | "Appointments Managed" | "Outbound Confirmation Rate";

const ApplicationTopHitsTable: React.FC<ApplicationTopHitsTableProps> = (props) => {
    const { getTagName, allTags, loadTagConfig } = useTagConfig();
    const sparkContainer = SparkContainer.useContainer();
    const sessionContainer = SessionAnalyticsContainer.useContainer();
    const telephonyAnalyticsContainer = TelephonyAnalyticsContainer.useContainer();
    const [isProcessingReviews, setIsProcessingReviews] = useState(false);
    const [uniqueEngagementCount, setUniqueEngagementCount] = useState<number>(-1);
    const [convertedCustomers, setConvertedCustomers] = useState<number>(-1);
    const [repeatCustomers, setRepeatCustomers] = useState<number>(-1);
    const [upsellPercentage, setUpsellPercentage] = useState<number>(-1);
    const [totalSold, setTotalSold] = useState<number>(0);
    const [averageOrderLength, setAverageOrderLength] = useState<number>(0);
    const [totalOrdersPlaced, setTotalOrdersPlaced] = useState<number>(0);
    const [bookedAppointmentsEnglish, setBookedAppointmentsEnglish] = useState<number>(0);
    const [bookedAppointmentsSpanish, setBookedAppointmentsSpanish] = useState<number>(0);
    const [rescheduledAppointmentsEnglish, setRescheduledAppointmentsEnglish] = useState<number>(0);
    const [rescheduledAppointmentsSpanish, setRescheduledAppointmentsSpanish] = useState<number>(0);
    const [cancelledAppointmentsEnglish, setCancelledAppointmentsEnglish] = useState<number>(0);
    const [cancelledAppointmentsSpanish, setCancelledAppointmentsSpanish] = useState<number>(0);
    const [checkedEnglish, setCheckedEnglish] = useState<number>(0)
    const [checkedSpanish, setCheckedSpanish] = useState<number>(0)
    const [inboundCountsLoaded, setInboundCountsLoaded] = useState<boolean>(false);
    const [hoursSaved, setHoursSaved] = useState<number>(0)
    const [loadingTopLevelMetrics, setLoadingTopLevelMetrics] = useState<boolean>(false)
    const [appTopLevelMetrics, setAppTopLevelMetrics] = useState<TopLevelMetrics | null>(null)
    const [outboundConfirmed, setOutboundConfirmed] = useState<number>(0);
    const [outboundCancelled, setOutboundCancelled] = useState<number>(0);
    const [outboundRescheduled, setOutboundRescheduled] = useState<number>(0);
    const [outboundLeftMessage, setOutboundLeftMessage] = useState<number>(0);
    const [outboundConfirmedEnglish, setOutboundConfirmedEnglish] = useState<number>(0);
    const [outboundConfirmedSpanish, setOutboundConfirmedSpanish] = useState<number>(0);
    const [outboundCancelledEnglish, setOutboundCancelledEnglish] = useState<number>(0);
    const [outboundCancelledSpanish, setOutboundCancelledSpanish] = useState<number>(0);
    const [outboundRescheduledEnglish, setOutboundRescheduledEnglish] = useState<number>(0);
    const [outboundRescheduledSpanish, setOutboundRescheduledSpanish] = useState<number>(0);
    const [outboundLeftMessageEnglish, setOutboundLeftMessageEnglish] = useState<number>(0);
    const [outboundLeftMessageSpanish, setOutboundLeftMessageSpanish] = useState<number>(0);
    const [hasSpanishOutboundCalls, setHasSpanishOutboundCalls] = useState(false);
    const [totalAppointments, setTotalAppointments] = useState<number>(0);
    const [totalConfirmed, setTotalConfirmed] = useState<number>(0);
    const [totalCancelled, setTotalCancelled] = useState<number>(0);
    const [totalReschedules, setTotalReschedules] = useState<number>(0);
    const [totalCheckedOut, setTotalCheckedOut] = useState<number>(0);
    const [totalLeftMessage, setTotalLeftMessage] = useState<number>(0);
    const [confirmedByVoicify, setConfirmedByVoicify] = useState<number>(0);
    const [leftMessageByVoicify, setLeftMessageByVoicify] = useState<number>(0);
    const [isLoadingAppointmentSummary, setIsLoadingAppointmentSummary] = useState<boolean>(false);
    const [appointmentSummaryFetchOk, setAppointmentSummaryFetchOk] = useState<boolean>(false);
    const [displayGraphModal, setDisplayGraphModal] = useState(false)
    const [graphCategories, setGraphCategories] = useState([])
    const [toggledGraphCategories, setToggledGraphCategories] = useState([])
    const [graphTags, setGraphTags] = useState([])
    const [toggledGraphTags, setToggledGraphTags] = useState([])
    const [appointmentsManagedView, setAppointmentsManagedView] = useState<'inbound' | 'outbound'>('inbound');
    const hasAutoSwitchedViewRef = useRef(false);
    const [chart, setChart] = useState<React.ReactNode>(
        <HistogramChartCard
            isLoading={true}
            title={null}
            data={null}
            showPrimary={true}
            showSecondary={false}
            secondaryData={null}
            primaryDataName='Volume'
        />);
    const [showActionCompletedHistogram, setShowActionCompletedHistogram] = useState(false);
    const [actionCompletedHistogram, setActionCompletedHistogram] = useState<React.ReactNode>(
        <HistogramChartCard
            isLoading={true}
            title={null}
            data={null}
            showPrimary={true}
            showSecondary={false}
            secondaryData={null}
            primaryDataName='Volume'
        />
    );

    const isMobile = useIsMobile();
    const parsedStartDate = useMemo(() => parseAnalyticsDate(props.startDate), [props.startDate]);
    const parsedEndDate = useMemo(() => parseAnalyticsDate(props.endDate), [props.endDate]);
    const parsedPreviousStartDate = useMemo(() => parseAnalyticsDate(props.previousDates.startDate), [props.previousDates.startDate]);
    const parsedPreviousEndDate = useMemo(() => parseAnalyticsDate(props.previousDates.endDate), [props.previousDates.endDate]);
    const engagementFilter = useMemo(() => {
        try {
            return buildTimezoneAwareDateFilter(props.startDate, props.endDate);
        } catch (error) {
            console.error('[ApplicationTopHitsTable] Invalid date range for engagement analytics', {
                startDate: props.startDate,
                endDate: props.endDate,
                error
            });
            return null;
        }
    }, [props.startDate, props.endDate]);

    useEffect(() => {
        if (props.applicationId) {
            loadTagConfig(props.applicationId);
        }
    }, [props.applicationId]);

    const [engagements, setEngagements] = useState<OrganizationEngagement[]>([]);
    const [engagementsLoaded, setEngagementsLoaded] = useState(false);

    const [containedCalls, setContainedCalls] = useState(0);
    const [containedCallsPercentage, setContainedCallPercentage] = useState<String | number>(0.0);
    const [outboundSuccessRate, setOutboundSuccessRate] = useState<string | number>(0);

    const assistantIndustry = props.application.featureFlags?.some(ff => ff.id && FoodOrderingFeatureFlagId.includes(ff.id)) ? "FOOD_ORDERING" :
        props.application.featureFlags?.some(ff => ff.id && HealthcareSchedulingFeatureFlagId.includes(ff.id)) ? "HEALTHCARE" : null;

    // Map engagements to SessionReview[] for downstream consumption
    const processedConversationalSuccessEvents = useMemo(() => {
        if (!engagementsLoaded || engagements.length === 0) return [];
        return mapEngagementsToSessionReviews(engagements, false);
    }, [engagements, engagementsLoaded]);

    // Compute and propagate engagement stats
    useEffect(() => {
        if (!engagementsLoaded) return;
        const stats = computeEngagementStats(engagements);
        setConvertedCustomers(stats.convertedCustomers);
        setRepeatCustomers(stats.repeatCustomers);
        setUniqueEngagementCount(stats.uniqueEngagementCount);
        setUpsellPercentage(stats.upsellPercentage);
    }, [engagements, engagementsLoaded]);

    const useGenerativeReview = props.application.featureFlags?.some(ff => ff.id && GENERATIVE_CALL_REVIEW_FEATURE_FLAG_IDS.includes(ff.id));
    const foodOrderingApp = props.application.featureFlags?.some(ff => ff.id && FoodOrderingFeatureFlagId.includes(ff.id));
    const healthcareApp = props.application.featureFlags?.some(ff => ff.id && HealthcareSchedulingFeatureFlagId.includes(ff.id));
    const isOutboundCallApp = props.application.featureFlags?.some(ff => ff.id === OutboundCallFeatureFlagId);
    const [hasSpanishCalls, setHasSpanishCalls] = useState(false);

    // Load generative report data when in Spark mode
    useEffect(() => {
        if (sparkContainer.inSpark && (props.startDate || props.endDate) && useGenerativeReview) {
            const filter = { startDate: props.startDate, endDate: props.endDate };
            sessionContainer.loadSessions(props.applicationId, filter);
            if (healthcareApp) {
                const minDurationSeconds = 10;
                const calculatedDaysBack = props.startDate && props.endDate
                    ? moment(props.endDate, DATE_FORMAT).diff(moment(props.startDate, DATE_FORMAT), 'days') + 1
                    : null;
                sessionContainer.loadSessionsForReview(props.applicationId, minDurationSeconds, calculatedDaysBack);
            }
            if (healthcareApp || foodOrderingApp) {
                // Load engagement records from the backend (replaces conversational success events)
                // Use timezone-aware dates so the server interprets them in the user's local timezone
                setEngagementsLoaded(false);
                if (!engagementFilter) {
                    setEngagements([]);
                    setEngagementsLoaded(true);
                    return;
                }
                let cancelledEngagements = false;
                loadEngagementsPaged(
                    getApplicationEngagements,
                    props.applicationId,
                    engagementFilter
                ).then(results => {
                    if (cancelledEngagements) return;
                    setEngagements(results);
                    setEngagementsLoaded(true);
                }).catch(() => {
                    if (cancelledEngagements) return;
                    setEngagements([]);
                    setEngagementsLoaded(true);
                });
                return () => { cancelledEngagements = true; };
            }
            if (foodOrderingApp && useGenerativeReview) {
                telephonyAnalyticsContainer.loadOrderPlacedEventsPaged(props.applicationId, filter);
                telephonyAnalyticsContainer.loadCallCompleteEventsPaged(props.applicationId, filter);
            }
        }
    }, [props.applicationId, props.startDate, props.endDate, sparkContainer.inSpark, useGenerativeReview, healthcareApp, foodOrderingApp, engagementFilter]);

    useEffect(() => {
        const ordersPlacedTotal = engagements.filter(event => event.sessionTags?.includes(TAGS.ACTION_COMPLETED))?.length ?? 0;
        setTotalOrdersPlaced(ordersPlacedTotal);
    }, [engagements])

    useEffect(() => {
        setHasSpanishCalls(engagements.filter(e => e.sessionTags?.includes(TAGS.SPANISH)).length > 0);
    }, [engagements])

    useEffect(() => {
        if (healthcareApp && engagementsLoaded) {
            // Count tag occurrences directly on raw engagements (matches DentalSchedulingDashboard).
            // This correctly handles multipatient bookings where one call may have multiple
            // occurrences of the same outcome tag.

            // Helper: count total occurrences of a tag across engagements
            const countTagOccurrences = (items: OrganizationEngagement[], tag: string) =>
                items.reduce((total, e) => {
                    const tags = e.sessionTags || [];
                    return total + tags.filter(t => t === tag).length;
                }, 0);

            // Inbound engagements for each type.
            // Rescheduled and Cancelled exclude OUTBOUND_CALL tagged engagements (matches DentalSchedulingDashboard lines 1407-1408).
            const scheduled = engagements.filter(e => (e.sessionTags || []).includes(TAGS.ACTION_COMPLETED));
            const rescheduled = engagements.filter(e => (e.sessionTags || []).includes(TAGS.APPOINTMENT_RESCHEDULED) && !(e.sessionTags || []).includes(TAGS.OUTBOUND_CALL));
            const cancelled = engagements.filter(e => (e.sessionTags || []).includes(TAGS.APPOINTMENT_CANCELLED) && !(e.sessionTags || []).includes(TAGS.OUTBOUND_CALL));
            const checkNextAppt = engagements.filter(e => (e.sessionTags || []).includes(TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE));

            const bookedEnglish = countTagOccurrences(scheduled.filter(e => !(e.sessionTags || []).includes(TAGS.SPANISH)), TAGS.ACTION_COMPLETED);
            const bookedSpanish = countTagOccurrences(scheduled.filter(e => (e.sessionTags || []).includes(TAGS.SPANISH)), TAGS.ACTION_COMPLETED);

            const rescheduledEnglish = countTagOccurrences(rescheduled.filter(e => !(e.sessionTags || []).includes(TAGS.SPANISH)), TAGS.APPOINTMENT_RESCHEDULED);
            const rescheduledSpanish = countTagOccurrences(rescheduled.filter(e => (e.sessionTags || []).includes(TAGS.SPANISH)), TAGS.APPOINTMENT_RESCHEDULED);

            const cancelledEnglish = countTagOccurrences(cancelled.filter(e => !(e.sessionTags || []).includes(TAGS.SPANISH)), TAGS.APPOINTMENT_CANCELLED);
            const cancelledSpanish = countTagOccurrences(cancelled.filter(e => (e.sessionTags || []).includes(TAGS.SPANISH)), TAGS.APPOINTMENT_CANCELLED);

            const checkedEnglishCount = countTagOccurrences(checkNextAppt.filter(e => !(e.sessionTags || []).includes(TAGS.SPANISH)), TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE);
            const checkedSpanishCount = countTagOccurrences(checkNextAppt.filter(e => (e.sessionTags || []).includes(TAGS.SPANISH)), TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE);

            setBookedAppointmentsEnglish(bookedEnglish);
            setBookedAppointmentsSpanish(bookedSpanish);

            setRescheduledAppointmentsEnglish(rescheduledEnglish);
            setRescheduledAppointmentsSpanish(rescheduledSpanish);

            setCancelledAppointmentsEnglish(cancelledEnglish);
            setCancelledAppointmentsSpanish(cancelledSpanish);

            setCheckedEnglish(checkedEnglishCount);
            setCheckedSpanish(checkedSpanishCount);
            setInboundCountsLoaded(true);
        }
    }, [engagements, engagementsLoaded, healthcareApp]);

    useEffect(() => {
        if (healthcareApp && engagementsLoaded) {
            // Use server-set isContainable/isContained flags on raw engagements (matches DentalSchedulingDashboard)
            // Exclude outbound calls from containment metrics
            const containableTotal = engagements.filter(e => e.isContainable && !e.sessionTags?.includes(TAGS.OUTBOUND_CALL)).length;
            const totalContained = engagements.filter(e => e.isContained && !e.sessionTags?.includes(TAGS.OUTBOUND_CALL)).length;

            setContainedCalls(totalContained);

            if (containableTotal <= 0) {
                setContainedCallPercentage("??");
            } else {
                const percentage = Math.round((totalContained / containableTotal) * 100);
                setContainedCallPercentage(percentage);
            }
        }
    }, [engagements, engagementsLoaded, healthcareApp])

    useEffect(() => {
        if (healthcareApp && isOutboundCallApp && processedConversationalSuccessEvents) {
            // Check if any outbound events have Spanish tag
            const outboundEvents = processedConversationalSuccessEvents.filter(e =>
                e.tags?.includes(TAGS.OUTBOUND_APPOINTMENT_CONFIRMED) ||
                e.tags?.includes(TAGS.OUTBOUND_APPOINTMENT_CANCELLED) ||
                e.tags?.includes(TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED) ||
                e.tags?.includes(TAGS.OUTBOUND_LEFT_MESSAGE)
            );
            setHasSpanishOutboundCalls(outboundEvents.some(e => e.tags?.includes(TAGS.SPANISH)));

            // Total counts
            const totalConfirmed = processedConversationalSuccessEvents.reduce((total, event) => total + (event.tags ? event.tags.filter(tag => tag === TAGS.OUTBOUND_APPOINTMENT_CONFIRMED).length : 0), 0);
            const totalLeftMessage = processedConversationalSuccessEvents.reduce((total, event) => total + (event.tags ? event.tags.filter(tag => tag === TAGS.OUTBOUND_LEFT_MESSAGE).length : 0), 0);
            setOutboundConfirmed(totalConfirmed);
            setOutboundLeftMessage(totalLeftMessage);

            // Legacy outbound correction: some older records have OUTBOUND_CALL + APPOINTMENT_CANCELLED/RESCHEDULED
            // but lack the dedicated OUTBOUND_APPOINTMENT_CANCELLED/RESCHEDULED tag.
            // Add these from raw engagements to avoid undercounting (matches DentalSchedulingDashboard pattern).
            const legacyCancelled = engagements.filter(e => {
                const tags = e.sessionTags || [];
                return tags.includes(TAGS.OUTBOUND_CALL) &&
                    tags.includes(TAGS.APPOINTMENT_CANCELLED) &&
                    !tags.includes(TAGS.OUTBOUND_APPOINTMENT_CANCELLED);
            });
            const legacyRescheduled = engagements.filter(e => {
                const tags = e.sessionTags || [];
                return tags.includes(TAGS.OUTBOUND_CALL) &&
                    tags.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
                    !tags.includes(TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED);
            });

            const legacyCancelledCount = legacyCancelled.reduce((total, e) => {
                return total + (e.sessionTags || []).filter(t => t === TAGS.APPOINTMENT_CANCELLED).length;
            }, 0);
            const legacyRescheduledCount = legacyRescheduled.reduce((total, e) => {
                return total + (e.sessionTags || []).filter(t => t === TAGS.APPOINTMENT_RESCHEDULED).length;
            }, 0);

            const dedicatedCancelled = processedConversationalSuccessEvents.reduce((total, event) => total + (event.tags ? event.tags.filter(tag => tag === TAGS.OUTBOUND_APPOINTMENT_CANCELLED).length : 0), 0);
            const dedicatedRescheduled = processedConversationalSuccessEvents.reduce((total, event) => total + (event.tags ? event.tags.filter(tag => tag === TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED).length : 0), 0);
            setOutboundCancelled(dedicatedCancelled + legacyCancelledCount);
            setOutboundRescheduled(dedicatedRescheduled + legacyRescheduledCount);

            // Success rate denominator: successful outcomes + notCaptured
            // (calls with OUTBOUND_CALL but no outcome tag — matches DentalSchedulingDashboard)
            const outboundOutcomeTags = [
                TAGS.OUTBOUND_APPOINTMENT_CONFIRMED,
                TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED,
                TAGS.OUTBOUND_APPOINTMENT_CANCELLED,
                TAGS.OUTBOUND_LEFT_MESSAGE,
                TAGS.OUTBOUND_CALL_TRANSFERRED,
            ];
            const outboundCallEvents = processedConversationalSuccessEvents.filter(e =>
                e.tags?.includes(TAGS.OUTBOUND_CALL)
            );
            const notCaptured = outboundCallEvents.filter(e =>
                !outboundOutcomeTags.some(t => e.tags?.includes(t))
            ).length;
            const successfulOutbound = outboundCallEvents.filter(e =>
                outboundOutcomeTags.some(t => e.tags?.includes(t))
            ).length + legacyCancelledCount + legacyRescheduledCount;
            const totalOutboundOutcomes = successfulOutbound + notCaptured;
            if (totalOutboundOutcomes === 0) {
                setOutboundSuccessRate("??");
            } else {
                setOutboundSuccessRate(Math.round((successfulOutbound / totalOutboundOutcomes) * 100));
            }

            // English/Spanish splits (same pattern as inbound)
            const confirmedEnglish = processedConversationalSuccessEvents.reduce((total, event) => {
                if (event.tags?.includes(TAGS.SPANISH) || !event.tags) return total;
                return total + event.tags.filter(tag => tag === TAGS.OUTBOUND_APPOINTMENT_CONFIRMED).length;
            }, 0);
            const confirmedSpanish = processedConversationalSuccessEvents.reduce((total, event) => {
                if (!event.tags?.includes(TAGS.SPANISH) || !event.tags) return total;
                return total + event.tags.filter(tag => tag === TAGS.OUTBOUND_APPOINTMENT_CONFIRMED).length;
            }, 0);

            const leftMessageEnglish = processedConversationalSuccessEvents.reduce((total, event) => {
                if (event.tags?.includes(TAGS.SPANISH) || !event.tags) return total;
                return total + event.tags.filter(tag => tag === TAGS.OUTBOUND_LEFT_MESSAGE).length;
            }, 0);
            const leftMessageSpanish = processedConversationalSuccessEvents.reduce((total, event) => {
                if (!event.tags?.includes(TAGS.SPANISH) || !event.tags) return total;
                return total + event.tags.filter(tag => tag === TAGS.OUTBOUND_LEFT_MESSAGE).length;
            }, 0);

            // Rescheduled split: dedicated tag (from processedEvents) + legacy correction (from raw engagements)
            const rescheduledEnglish = processedConversationalSuccessEvents.reduce((total, event) => {
                if (event.tags?.includes(TAGS.SPANISH) || !event.tags) return total;
                return total + event.tags.filter(tag => tag === TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED).length;
            }, 0) + legacyRescheduled.filter(e => !(e.sessionTags || []).includes(TAGS.SPANISH)).reduce((total, e) => {
                return total + (e.sessionTags || []).filter(t => t === TAGS.APPOINTMENT_RESCHEDULED).length;
            }, 0);
            const rescheduledSpanish = processedConversationalSuccessEvents.reduce((total, event) => {
                if (!event.tags?.includes(TAGS.SPANISH) || !event.tags) return total;
                return total + event.tags.filter(tag => tag === TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED).length;
            }, 0) + legacyRescheduled.filter(e => (e.sessionTags || []).includes(TAGS.SPANISH)).reduce((total, e) => {
                return total + (e.sessionTags || []).filter(t => t === TAGS.APPOINTMENT_RESCHEDULED).length;
            }, 0);

            // Cancelled split: dedicated tag (from processedEvents) + legacy correction (from raw engagements)
            const cancelledEnglish = processedConversationalSuccessEvents.reduce((total, event) => {
                if (event.tags?.includes(TAGS.SPANISH) || !event.tags) return total;
                return total + event.tags.filter(tag => tag === TAGS.OUTBOUND_APPOINTMENT_CANCELLED).length;
            }, 0) + legacyCancelled.filter(e => !(e.sessionTags || []).includes(TAGS.SPANISH)).reduce((total, e) => {
                return total + (e.sessionTags || []).filter(t => t === TAGS.APPOINTMENT_CANCELLED).length;
            }, 0);
            const cancelledSpanish = processedConversationalSuccessEvents.reduce((total, event) => {
                if (!event.tags?.includes(TAGS.SPANISH) || !event.tags) return total;
                return total + event.tags.filter(tag => tag === TAGS.OUTBOUND_APPOINTMENT_CANCELLED).length;
            }, 0) + legacyCancelled.filter(e => (e.sessionTags || []).includes(TAGS.SPANISH)).reduce((total, e) => {
                return total + (e.sessionTags || []).filter(t => t === TAGS.APPOINTMENT_CANCELLED).length;
            }, 0);

            setOutboundConfirmedEnglish(confirmedEnglish);
            setOutboundConfirmedSpanish(confirmedSpanish);
            setOutboundLeftMessageEnglish(leftMessageEnglish);
            setOutboundLeftMessageSpanish(leftMessageSpanish);
            setOutboundRescheduledEnglish(rescheduledEnglish);
            setOutboundRescheduledSpanish(rescheduledSpanish);
            setOutboundCancelledEnglish(cancelledEnglish);
            setOutboundCancelledSpanish(cancelledSpanish);
        }
    }, [processedConversationalSuccessEvents, engagements, healthcareApp, isOutboundCallApp]);

    // Reset auto-switch flag when app or date range changes so a fresh data load re-evaluates
    useEffect(() => {
        hasAutoSwitchedViewRef.current = false;
        setInboundCountsLoaded(false);
    }, [props.applicationId, props.startDate, props.endDate]);

    // Auto-switch to outbound view if inbound data is empty after loading completes
    useEffect(() => {
        if (!isOutboundCallApp || !healthcareApp) return;
        if (!engagementsLoaded) return;
        if (!inboundCountsLoaded) return;
        if (hasAutoSwitchedViewRef.current) return;

        const inboundTotal = bookedAppointmentsEnglish + bookedAppointmentsSpanish +
            rescheduledAppointmentsEnglish + rescheduledAppointmentsSpanish +
            cancelledAppointmentsEnglish + cancelledAppointmentsSpanish +
            checkedEnglish + checkedSpanish;

        hasAutoSwitchedViewRef.current = true;
        if (inboundTotal === 0) {
            setAppointmentsManagedView('outbound');
        }
    }, [engagementsLoaded, inboundCountsLoaded, bookedAppointmentsEnglish, bookedAppointmentsSpanish,
        rescheduledAppointmentsEnglish, rescheduledAppointmentsSpanish,
        cancelledAppointmentsEnglish, cancelledAppointmentsSpanish,
        checkedEnglish, checkedSpanish, isOutboundCallApp, healthcareApp]);

    useEffect(() => {
        if (!isOutboundCallApp || !healthcareApp || !props.organizationId || !props.startDate || !props.endDate) return;
        let cancelled = false;
        const load = async () => {
            setIsLoadingAppointmentSummary(true);
            setAppointmentSummaryFetchOk(false);
            const result = await getAppointmentSummary(props.organizationId, {
                applicationIds: [props.applicationId],
                dateFrom: props.startDate,
                dateTo: props.endDate,
            });
            if (cancelled) return;
            if (result.resultType === 'Ok') {
                setTotalAppointments(result.data.totalAppointments);
                setTotalConfirmed(result.data.totalConfirmed);
                setTotalCancelled(result.data.totalCancelled);
                setTotalReschedules(result.data.totalReschedules);
                setTotalCheckedOut(result.data.totalCheckedOut ?? 0);
                setTotalLeftMessage(result.data.totalLeftMessage ?? 0);
                setConfirmedByVoicify(result.data.confirmedByVoicify);
                setLeftMessageByVoicify(result.data.leftMessageByVoicify);
                setAppointmentSummaryFetchOk(true);
            }
            setIsLoadingAppointmentSummary(false);
        };
        load();
        return () => { cancelled = true; };
    }, [props.organizationId, props.applicationId, props.startDate, props.endDate, isOutboundCallApp, healthcareApp]);

    const appointmentResponseRate = useMemo((): number | null => {
        const denominator = totalAppointments - totalLeftMessage;
        if (denominator <= 0) return null;
        const responded = totalConfirmed + totalCancelled + totalReschedules + totalCheckedOut;
        return Math.round((responded / denominator) * 100);
    }, [totalConfirmed, totalCancelled, totalReschedules, totalCheckedOut, totalLeftMessage, totalAppointments]);

    // Load top level metrics from API for Staff/Labor Hours Saved calculation
    useEffect(() => {
        if (sparkContainer.inSpark && (props.startDate || props.endDate) && useGenerativeReview && (healthcareApp || foodOrderingApp)) {
            const loadMetrics = async () => {
                setLoadingTopLevelMetrics(true);
                if (!engagementFilter) {
                    setLoadingTopLevelMetrics(false);
                    setAppTopLevelMetrics(null);
                    return;
                }
                const metricsFilter: EngagementAnalyticsFilter = engagementFilter;
                const result = await getTopLevelMetrics(props.applicationId, metricsFilter);
                setLoadingTopLevelMetrics(false);
                if (result.resultType === "Ok") {
                    setAppTopLevelMetrics(result.data);
                }
            };
            loadMetrics();
        }
    }, [props.applicationId, props.startDate, props.endDate, sparkContainer.inSpark, useGenerativeReview, healthcareApp, foodOrderingApp, engagementFilter]);

    // Calculate hours saved from API's laborMinutesSaved
    useEffect(() => {
        if (appTopLevelMetrics?.laborMinutesSaved !== undefined && appTopLevelMetrics?.laborMinutesSaved !== null) {
            // Convert minutes to hours
            const hours = appTopLevelMetrics.laborMinutesSaved / 60;
            setHoursSaved(hours);
        }
        if (foodOrderingApp) {
            if (appTopLevelMetrics?.totalRevenue !== undefined && appTopLevelMetrics?.totalRevenue !== null || (appTopLevelMetrics?.totalOrdersCompleted !== undefined && appTopLevelMetrics?.totalOrdersCompleted !== null && appTopLevelMetrics?.averageTicketSize !== undefined && appTopLevelMetrics?.averageTicketSize !== null)) {
                const revenue = appTopLevelMetrics?.totalRevenue ?? ((appTopLevelMetrics?.totalOrdersCompleted ?? 0) * (appTopLevelMetrics?.averageTicketSize ?? 0));
                setTotalSold(revenue);
            }
        }
    }, [appTopLevelMetrics]);

    // Calculate total sold from order placed events (food ordering specific metrics)
    useEffect(() => {
        // get all call complete events that have a sessionId in the orderPlacedEvents
        const orderPlacedCompleteEvents = telephonyAnalyticsContainer.callCompleteEvents.filter(event =>
            telephonyAnalyticsContainer.orderPlacedEvents.some(orderEvent => orderEvent.sessionId === event.sessionId)
        );
        // add up duration property for all seconds
        const totalDuration = orderPlacedCompleteEvents.reduce((acc, event) => acc + (event.duration || 0), 0);
        const avgOrder = totalDuration / (orderPlacedCompleteEvents.length || 1);
        setAverageOrderLength(avgOrder);
    }, [telephonyAnalyticsContainer.orderPlacedEvents, telephonyAnalyticsContainer.callCompleteEvents, appTopLevelMetrics]);

    useEffect(() => {
        setChart(<HistogramChartCard
            isLoading={true}
            title={null}
            data={null}
            showPrimary={true}
            showSecondary={false}
            secondaryData={null}
            primaryDataName='Volume'
        />)
        let cancelled = false;
        (async () => {
            const node = await renderGraph(graphCategories, graphTags, true);
            if (!cancelled) setChart(node);
        })();
        return () => { cancelled = true; };
    }, [graphCategories, graphTags, props.startDate, props.endDate]);

    useEffect(() => {
        (async () => {
            const actionCompletedNode = await renderGraph(["ACTION_ATTEMPTED"], ["ACTION_COMPLETED"], false);
            setActionCompletedHistogram(actionCompletedNode);
        })();
    }, [props.applicationId, props.startDate, props.endDate, sparkContainer.inSpark]);


    const toggleGraphTag = (tag: string) => {
        setToggledGraphTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        )
    };

    const toggleGraphCategories = (cat: string) => {
        setToggledGraphCategories(prev =>
            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
        );
    };

    const GraphFilterButton = () => {
        return (
            <GraphButtonDiv>
                <GraphButton onClick={() => setDisplayGraphModal(true)} themes={['primary']} text='Customize' />
            </GraphButtonDiv>
        )
    }

    const HistogramGraph: React.FC<{
        includeButton: boolean;
        categories: string[];
        tags: string[];
        featureFlagId?: string;
        applicationId: string;
        startDate: string;
        endDate: string;
        onOpenFilters: () => void;
    }> = ({
        includeButton,
        categories,
        tags,
        featureFlagId,
        applicationId,
        startDate,
        endDate,
        onOpenFilters
    }) => {
            const { title, mappedData, setParams } =
                HistogramContainer.useContainer();

            // Keep the container params in sync with the props passed to this component
            React.useEffect(() => {
                setParams(p => ({
                    ...p,
                    applicationId,
                    startDate,
                    endDate,
                    categories,
                    tags,
                    featureFlagId
                }));
            }, [applicationId, startDate, endDate, categories, tags, featureFlagId, setParams]);
            return (
                <HistogramChartCard
                    isLoading={false}
                    title={title}
                    data={mappedData}
                    showPrimary={true}
                    showSecondary={false}
                    secondaryData={null}
                    primaryDataName="Volume"
                    filterButton={includeButton ? GraphFilterButton() : undefined}
                    titleMargin={{ top: 16, right: 0, bottom: 0, left: 0 }}
                    titlePadding={{ top: 0, right: 0, bottom: 0, left: 26 }}
                    graphPadding={4}
                    graphBottomPadding={4}
                    titleStyle={histogramTitleStyle}
                />
            )
        }

    const renderGraph = async (categories: string[], tags: string[], includeButton: boolean) => {
        return <HistogramContainer.Provider
            initialState={{
                applicationId: props.applicationId,
                startDate: props.startDate,   // local naive is fine; container converts to UTC
                endDate: props.endDate,
                categories: categories,
                tags: tags,
                featureFlagId: assistantIndustry,
            }}
        >
            <HistogramGraph
                includeButton={includeButton}
                categories={categories}
                tags={tags}
                featureFlagId={assistantIndustry}
                applicationId={props.applicationId}
                startDate={props.startDate}
                endDate={props.endDate}
                onOpenFilters={() => setDisplayGraphModal(true)}
            />
        </HistogramContainer.Provider>
    }

    const sparkMobileColumns = [
        {
            id: "topicInquired",
            Header: <ReportTableHeader title="Topic Inquired" showPercentage={false} showValue={false} />,
            accessor: (item: ContentHitModel) => item,
            Cell: row => (
                <TableCell>
                    <img className="feature-type-icon" src={getSmallFeatureTypeIcon((row.value.featureTypeId == null || row.value.featureTypeId == "null") ? FallbackFeatureTypeId : row.value.featureTypeId)} />
                    <p className="ac-analytics-report-content-item">{row.value.title ?? "Deleted Topic"}</p>
                </TableCell>
            )
        },
        {
            id: "transcripts",
            Header: <ReportTableHeader title='# Transcripts' showValue={false} showPercentage={false} />,
            accessor: (item: ContentHitModel) => item,
            Cell: (row: { value: ContentHitModel }) => {
                let baseUrl: string;
                sparkContainer.inSpark ? baseUrl = getSparkBaseUrl() : baseUrl = getBaseUrl();
                return <TableCell>
                    <a
                        style={{ color: color_text_link }}
                        href={`${baseUrl}/v/apps/${props.applicationId}/analytics/sessions?contentId=${row.value.contentId}&startDate=${props.startDate}&endDate=${props.endDate}`} target="_blank"
                    >
                        Transcript{row.value.sessionCount > 1 ? "s" : ""} ({row.value.sessionCount})
                    </a>
                </TableCell>
            }
        }
    ]
    const sparkColumns = [
        {
            id: "topicInquired",
            Header: <ReportTableHeader title="Topic Inquired" showPercentage={false} showValue={false} />,
            accessor: (item: ContentHitModel) => item,
            Cell: row => (
                <TableCell>
                    <img className="feature-type-icon" src={getSmallFeatureTypeIcon((row.value.featureTypeId == null || row.value.featureTypeId == "null") ? FallbackFeatureTypeId : row.value.featureTypeId)} />
                    <p className="ac-analytics-report-content-item">{row.value.title ?? "Deleted Topic"}</p>
                </TableCell>
            )
        },
        {
            id: "callerCount",
            Header: <ReportTableHeader title='Unique Callers' showValue={false} showPercentage={false} />,
            accessor: (item: ContentHitModel) => item.userCount,
            Cell: row => (
                <TableCell>
                    {row.value}
                </TableCell>
            )
        },
        {
            id: "transcripts",
            Header: <ReportTableHeader title='# Transcripts' showValue={false} showPercentage={false} />,
            accessor: (item: ContentHitModel) => item,
            Cell: (row: { value: ContentHitModel }) => {
                let baseUrl: string;
                sparkContainer.inSpark ? baseUrl = getSparkBaseUrl() : baseUrl = getBaseUrl();
                return <TableCell>
                    <StyledLink
                        href={`${baseUrl}/v/apps/${props.applicationId}/analytics/sessions?contentId=${row.value.contentId}&startDate=${props.startDate}&endDate=${props.endDate}`} target="_blank"
                    >
                        Transcript{row.value.sessionCount > 1 ? "s" : ""} ({row.value.sessionCount})
                    </StyledLink>
                </TableCell>
            }
        }
    ];


    const deltaUsers = (props.totals?.totalUsers ?? 0) - (props.previousTotals?.totalUsers ?? 0);
    const deltaNewUsers = (props.totals?.totalNewUsers ?? 0) - (props.previousTotals?.totalNewUsers ?? 0);
    const deltaSessions = (props.totals?.totalSessions ?? 0) - (props.previousTotals?.totalSessions ?? 0);
    const deltaRequests = (props.filteredInteractionsCount ?? 0) - (props.previousTotals?.totalRequests ?? 0);
    const deltaInteractions = (props.filteredInteractionsCount ?? 0) - (props.previousInteractionsCount ?? 0);
    const deltaMinutes = (props.totals?.totalMinutes ?? 0) - (props.previousTotals?.totalMinutes ?? 0);
    const deltaNumberOfCaller = (props.totals?.totalUsers ?? 0) - (props.previousTotals?.totalUsers ?? 0);

    const timeframeLabel = `${parsedStartDate.isValid() ? parsedStartDate.format("MMM DD, YYYY") : props.startDate} - ${parsedEndDate.isValid() ? parsedEndDate.format("MMM DD, YYYY") : props.endDate}`;
    const filter = engagementFilter ?? { startDate: props.startDate, endDate: props.endDate, timezone: undefined };
    const numberOfDays = parsedStartDate.isValid() && parsedEndDate.isValid()
        ? parsedEndDate.diff(parsedStartDate, 'days') + 1
        : 1;

    // Spark: average calls per day
    const previousNumberOfDays = parsedPreviousStartDate.isValid() && parsedPreviousEndDate.isValid()
        ? parsedPreviousEndDate.diff(parsedPreviousStartDate, 'days') + 1
        : 1;
    const averageCallsPerDay = Math.round((props.totals?.totalSessions ?? 0) / numberOfDays);
    const previousAverageCallsPerDay = Math.round((props.previousTotals?.totalSessions ?? 0) / previousNumberOfDays);
    const deltaAverageCallsPerDay = averageCallsPerDay - previousAverageCallsPerDay;

    // Spark: average call length
    const averageCallLength = (props.totals?.totalMinutes ?? 0) / (props.totals?.totalSessions ?? 0)
    const previousAverageCallLength = (props.previousTotals?.totalSessions ?? 0) / (props.previousTotals?.totalMinutes ?? 0)
    const deltaAverageCallLength = averageCallLength - previousAverageCallLength;

    // Spark: average topic inquiries per call
    const averageTopicInquiriesPerCall = (props.filteredInteractionsCount ?? 0) / (props.totals?.totalSessions ?? 1) || 0;
    const previousAverageTopicInquiriesPerCall = (props.previousTotals?.totalRequests ?? 0) / (props.previousTotals?.totalSessions ?? 1) || 0;
    const deltaAverageTopicInquiriesPerCall = (props.previousTotals?.totalSessions ?? 0) === 0 ? 0 : averageTopicInquiriesPerCall - previousAverageTopicInquiriesPerCall;
    const deltaAverageTopicInquiriesPerCallPercentage = (previousAverageTopicInquiriesPerCall > 0) ? Math.round(deltaAverageTopicInquiriesPerCall / previousAverageTopicInquiriesPerCall * 100) : 0;

    const getExportableData = () => {
        const rows = [];
        rows.push([`"${timeframeLabel}"`, `Total Users: ${props.totals?.totalUsers ?? 0}`, `Total New Users: ${props.totals?.totalNewUsers ?? 0}`, `Total Sessions: ${props?.totals?.totalSessions ?? 0}`, `Total Interactions: ${props.totals?.totalSessions ?? 0}`])
        rows.push([]);
        rows.push(['Item name', 'Users', 'New Users', 'Sessions', 'Interactions']);
        props.data.items.forEach(item => {
            rows.push([item.title, item.userCount, item.newUserCount, item.sessionCount, item.count]);
        })
        return rows;
    };

    const columns = [
        {
            id: "title",
            Header: <ReportTableHeader title="Conversation Item" showPercentage={false} showValue={false} />,
            accessor: (item: ContentHitModel) => item,
            Cell: row => (
                <TableCell>
                    <img className="feature-type-icon" src={getSmallFeatureTypeIcon((row.value.featureTypeId == null || row.value.featureTypeId == "null") ? FallbackFeatureTypeId : row.value.featureTypeId)} />
                    <a className="ac-analytics-report-content-item" href={getEditUrl(row.value.contentId, row.value.applicationFeatureId, (row.value.featureTypeId == null || row.value.featureTypeId == "null") ? FallbackFeatureTypeId : row.value.featureTypeId) + 'analytics' + window.location.search} target="_blank">{row.value.title ?? "No Title"}</a>
                </TableCell>
            )
        },
        {
            id: "userCount",
            Header: <ReportTableHeader title='Users' showValue={false} showPercentage={false} />,
            accessor: (item: ContentHitModel) => item.userCount,
            Cell: row => (
                <TableCell>
                    {row.value}
                </TableCell>
            )
        },
        {
            id: "newUserCount",
            Header: <ReportTableHeader title='New Users' showValue={false} showPercentage={false} />,
            accessor: (item: ContentHitModel) => item.newUserCount,
            Cell: row => (
                <TableCell>
                    {row.value}
                </TableCell>
            )
        },
        {
            id: "sessionCount",
            Header: <ReportTableHeader title='Sessions' showValue={false} showPercentage={false} />,
            accessor: (item: ContentHitModel) => item.sessionCount,
            Cell: row => (
                <TableCell>
                    {row.value}
                </TableCell>
            )
        },
        {
            id: "count",
            Header: <ReportTableHeader title='Interactions' showValue={false} showPercentage={false} />,
            accessor: (item: ContentHitModel) => item.count,
            Cell: row => (
                <TableCell>
                    {row.value}
                </TableCell>
            )
        }
    ];

    function everythingIsLoading() {
        if (sparkContainer.inSpark) {
            // In Spark mode, only gate on props-level loading and appointment summary.
            // Do NOT gate on sessionContainer.loading or isProcessingReviews here —
            // GenerativeReportTable manages its own loading UI internally. Including
            // them causes a chicken-and-egg: sessionContainer.loading=true unmounts
            // the table, which prevents isProcessingReviews from ever resetting to false.
            const engagementDataLoading = (healthcareApp || foodOrderingApp) && useGenerativeReview && !engagementsLoaded;
            return !Object.values(props.isLoading).includes(false) ||
                engagementDataLoading ||
                isLoadingAppointmentSummary;
        }
        return !Object.values(props.isLoading).includes(false) || isLoadingAppointmentSummary;
    }

    const items = props.data?.items?.filter(i => i.featureTypeId && !i.featureTypeId?.startsWith("response"));

    function renderTable() {
        if (props.isLoading.contentHits) {
            return <div className="loader-container"><Loader /></div>
        }

        if (sparkContainer.inSpark) {
            if (useGenerativeReview) {
                return <GenerativeReportTable
                    applicationId={props.applicationId}
                    filter={filter}
                    application={props.application}
                    sessionContainer={sessionContainer}
                    setUniqueEngagementCount={setUniqueEngagementCount}
                    setConvertedCustomers={setConvertedCustomers}
                    orderPlacedEvents={telephonyAnalyticsContainer.orderPlacedEvents}
                    onProcessingChange={setIsProcessingReviews}
                    setUpsellPercentage={setUpsellPercentage}
                    setRepeatCustomers={setRepeatCustomers}
                />
            } else {
                const sparkItems = props.data?.items?.filter(i => !SPARK_EXCLUDED_FEATURE_TYPE_IDS.includes(i.featureTypeId));
                return <ReportTable
                    className={containerStyle}
                    columns={isMobile ? sparkMobileColumns : sparkColumns}
                    data={sparkItems}
                    showPagination={false}
                    getExportableData={getExportableData}
                    exportName={`${props.startDate}-${props.endDate}_ContentHits`}
                />
            }
        }
        return <ReportTable
            className={containerStyle}
            columns={columns}
            data={items}
            showPagination={false}
            getExportableData={getExportableData}
            exportName={`${props.startDate}-${props.endDate}_ContentHits`}
        />
    }

    const formatNumber = (num: number) => {
        if (num === undefined) return;
        return num.toLocaleString(undefined, { maximumFractionDigits: 2 })
    }

    const formatSecondsToMinutes = (s: number) => {
        if (s === undefined) return;
        // turn 123 seconds into 2 minutes and 3 seconds 61 into one minute and 1 second
        const minutes = Math.floor(s / 60);
        const seconds = Math.round(s % 60);
        if (minutes === 0) {
            return `${seconds} seconds`;
        }
        return `${minutes} minute${minutes > 1 ? 's' : ''} ${seconds} second${seconds > 1 ? 's' : ''}`;
    }

    const formatDollarsAndCents = (num: number) => {
        return num.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    const formatSeconds = (seconds: number) => {
        if (seconds === undefined) return;
        return Math.round(seconds);
    }

    const formatHoursAndMinutes = (hours: number) => {
        // convert hour with decimal to minutes with colon
        if (hours === undefined) return;
        const wholeHours = Math.floor(hours);
        const minutes = Math.round((hours - wholeHours) * 60);
        if (wholeHours === 0) {
            return `${minutes} minutes`;
        }
        return `${wholeHours} hour${wholeHours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
    }

    // Use the compact format for Staff/Labor Hours Saved tiles
    const formattedHoursSaved = formatHoursSaved(hoursSaved);

    const formatEngagementsWithCalls = (engagements: number) => {
        if (engagements === undefined || engagements < 0) return "?";
        const totalCalls = props.totals?.totalSessions || 0;
        return `${engagements} in ${totalCalls} call${totalCalls !== 1 ? 's' : ''}`;
    }

    const getActionCompletedValue = () => {
        if (foodOrderingApp) {
            return totalOrdersPlaced > -1 ? totalOrdersPlaced : "?";
        } else if (healthcareApp) {
            const actionCompleted = bookedAppointmentsEnglish + bookedAppointmentsSpanish + rescheduledAppointmentsEnglish + rescheduledAppointmentsSpanish + cancelledAppointmentsEnglish + cancelledAppointmentsSpanish + checkedEnglish + checkedSpanish;
            return actionCompleted > -1 ? actionCompleted : "?";
        }
    };

    const getActionCompletedSubValues = (subValueNumber: number) => {
        if (foodOrderingApp) {
            if (subValueNumber === 1) {
                return (
                    <>
                        Total sold: <BoldValueSpan>
                            {formatDollarsAndCents(totalSold)}
                        </BoldValueSpan>
                    </>
                );
            } else if (subValueNumber === 2) {
                return (
                    <>
                        Average ticket size: <BoldValueSpan>
                            {formatDollarsAndCents(totalSold / (totalOrdersPlaced > 0 ? totalOrdersPlaced : 1))}
                        </BoldValueSpan>
                    </>
                );
            } else if (subValueNumber === 3) {
                return (
                    <>
                        Average order length: {averageOrderLength > 60 ?
                            `${formatSecondsToMinutes(averageOrderLength)}`
                            : `${formatNumber(formatSeconds(averageOrderLength))} seconds`}
                    </>
                );
            }
        } else if (healthcareApp) {
            if (subValueNumber === 1) {
                return (
                    <>
                        <BoldValueSpan>
                            {bookedAppointmentsEnglish > -1 ? bookedAppointmentsEnglish : "?"}
                        </BoldValueSpan>
                    </>
                );
            } else if (subValueNumber === 2) {
                return (
                    <>
                        <BoldValueSpan>
                            {rescheduledAppointmentsEnglish > -1 ? rescheduledAppointmentsEnglish : "?"}
                        </BoldValueSpan>
                    </>
                );
            } else if (subValueNumber === 3) {
                return (
                    <>
                        <BoldValueSpan>
                            {cancelledAppointmentsEnglish > -1 ? cancelledAppointmentsEnglish : "?"}
                        </BoldValueSpan>
                    </>
                );
            } else if (subValueNumber === 4) {
                return (
                    <>
                        <BoldValueSpan>
                            {checkedEnglish > -1 ? checkedEnglish : "?"}
                        </BoldValueSpan>
                    </>
                );
            }
        }
        return null;
    };

    const getActionCompletedSubValuesSpanish = (subValueNumber: number) => {
        if (foodOrderingApp) {
            if (subValueNumber === 1) {
                return (
                    <>
                        Total sold: <BoldValueSpan>
                            {formatDollarsAndCents(totalSold)}
                        </BoldValueSpan>
                    </>
                );
            } else if (subValueNumber === 2) {
                return (
                    <>
                        Average ticket size: <BoldValueSpan>
                            {formatDollarsAndCents(totalSold / (telephonyAnalyticsContainer.orderPlacedEvents?.length > 0 ? telephonyAnalyticsContainer.orderPlacedEvents?.length : 1))}
                        </BoldValueSpan>
                    </>
                );
            } else if (subValueNumber === 3) {
                return (
                    <>
                        Average order length: {averageOrderLength > 60 ?
                            `${formatSecondsToMinutes(averageOrderLength)}`
                            : `${formatNumber(formatSeconds(averageOrderLength))} seconds`}
                    </>
                );
            }
        } else if (healthcareApp) {
            if (subValueNumber === 1) {
                return (
                    <>
                        <BoldValueSpan>
                            {bookedAppointmentsSpanish > -1 ? bookedAppointmentsSpanish : "?"}
                        </BoldValueSpan>
                    </>
                );
            } else if (subValueNumber === 2) {
                return (
                    <>
                        <BoldValueSpan>
                            {rescheduledAppointmentsSpanish > -1 ? rescheduledAppointmentsSpanish : "?"}
                        </BoldValueSpan>
                    </>
                );
            } else if (subValueNumber === 3) {
                return (
                    <>
                        <BoldValueSpan>
                            {cancelledAppointmentsSpanish > -1 ? cancelledAppointmentsSpanish : "?"}
                        </BoldValueSpan>
                    </>
                );
            } else if (subValueNumber === 4) {
                return (
                    <>
                        <BoldValueSpan>
                            {checkedSpanish > -1 ? checkedSpanish : "?"}
                        </BoldValueSpan>
                    </>
                );
            }
        }
        return null;
    };

    const getTooltip = (title: StyledTotalCardTitle) => {
        if (foodOrderingApp) {
            switch (title) {
                case "Engagements":
                    return 'Calls under 10 minutes apart from the same customer are grouped as one engagement.';
                case "Orders Placed":
                    return 'Total number of orders placed with the assistant during this timeframe.';
                case "Repeat Customers":
                    return 'Customers that placed multiple orders with the assistant during this timeframe.';
                case "Converted Customers":
                    return 'Customers that placed an order after earlier transferring to the store.';
                case "Upsell Percentage":
                    return 'Out of calls where we attempted an upsell, the percentage of those that were successful.';
                case "Labor Hours Saved":
                    return 'Amount of time the assistant spent taking placed orders.';
            }

        } else if (healthcareApp) {
            switch (title) {
                case "Engagements":
                    return 'Calls under 10 minutes apart from the same customer are grouped as one engagement.';
                case "Appointment Updates":
                    return 'Total number of Appointment Updates with the assistant during this timeframe.';
                case "Repeat Customers":
                    return 'Patients that booked multiple appointments with the assistant during this timeframe.';
                case "Converted Customers":
                    return 'Patients that made an appointment update after earlier transferring to the office.';
                case "Labor Hours Saved":
                case "Staff Hours Saved":
                    return 'Amount of time the assistant spent scheduling appointments.';
                case "Average Call Length":
                    return '';
                case "Call Containment":
                    return "Calls that were either fielded successfully or transferred due to an unsupported request.";
                case "Outbound Confirmation Rate":
                    return "Percentage of outbound calls that resulted in any captured outcome: Confirmed, Rescheduled, Cancelled, Left Message, or Transferred.";
            }
        }

    };

    const getSubValue = (title: StyledTotalCardTitle) => {
        if (foodOrderingApp) {
            switch (title) {
                case "Engagements":
                    return 'Calls under 10 minutes apart from the same customer are grouped as one engagement.';
                case "Repeat Customers":
                    return 'Customers that placed multiple orders with the assistant during this timeframe.';
                case "Converted Customers":
                    return 'Customers that initially requested staff but ultimately ordered with the assistant.';
                case "Upsell Percentage":
                    return 'of attempted upsells were successful!';
                case "Labor Hours Saved":
                    return 'Ordering time the assistant took off your team’s plate.';
            }
        } else if (healthcareApp) {
            switch (title) {
                case "Engagements":
                    return 'Calls under 10 minutes apart from the same customer are grouped as one engagement.';
                // return `in ${props.totals?.totalSessions || 0} calls`;
                case "Repeat Customers":
                    return 'Patients that booked multiple appointments with the assistant during this timeframe.';
                case "Converted Customers":
                    return 'Patients that initially requested staff but ultimately booked with the assistant.';
                case "Labor Hours Saved":
                case "Staff Hours Saved":
                    return 'Time the assistant saved your receptionists.';
                case "Average Call Length":
                    return 'Average length of calls handled by the assistant.';
            }
        }
    };

    const getIcon = (title: StyledTotalCardTitle) => {
        if (foodOrderingApp) {
            switch (title) {
                case "Engagements":
                    return phoneIconBlue;
                case "Orders Placed":
                    return orderPlacedIconBlue;
                case "Repeat Customers":
                    return repeatCustomersIcon;
                case "Converted Customers":
                    return convertedCustomersIcon;
                case "Upsell Percentage":
                    return upsellPercentageIcon;
                case "Labor Hours Saved":
                    return laborHoursSavedIcon;
            }
        } else if (healthcareApp) {
            switch (title) {
                case "Engagements":
                    return phoneIconBlue;
                case "Appointment Updates":
                    return appointmentUpdatesIcon;
                case "Appointments Managed":
                    return null;
                case "Repeat Customers":
                    return repeatCustomersIcon;
                case "Converted Customers":
                    return convertedCustomersIcon;
                case "Labor Hours Saved":
                case "Staff Hours Saved":
                    return laborHoursSavedIcon;
            }
        }
    }

    const actionCompletedTitle: StyledTotalCardTitle = foodOrderingApp ? 'Orders Placed' : 'Appointments Managed';
    const hoursSavedTitle: StyledTotalCardTitle = healthcareApp ? 'Staff Hours Saved' : 'Labor Hours Saved';

    const getOutboundTotalValue = () => {
        const total = (outboundConfirmed ?? 0) + (outboundLeftMessage ?? 0) + (outboundRescheduled ?? 0) + (outboundCancelled ?? 0);
        return total;
    };

    const renderGenerativeReviewCards = () => {
        // Build the title with inline toggle button for outbound apps
        const appointmentsManagedTitleWithToggle = isOutboundCallApp && healthcareApp ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                Appointments Managed
                <InlineViewToggle
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setAppointmentsManagedView(prev => prev === 'inbound' ? 'outbound' : 'inbound');
                    }}
                >
                    {appointmentsManagedView === 'inbound' ? '→ Outbound' : '→ Inbound'}
                </InlineViewToggle>
            </span>
        ) : actionCompletedTitle;

        return (
            // healthcareApp ? (
            <StyledTopMetricsContainer>
                <TopCardsContainer>
                    {isOutboundCallApp && appointmentsManagedView === 'outbound' && healthcareApp ? (
                        <StyledTopCard
                            title={appointmentsManagedTitleWithToggle}
                            value={getOutboundTotalValue()}
                            deltaValue={null}
                            deltaPercentage={null}
                            showValue={false}
                            showPercentage={false}
                            hasSpanishCalls={hasSpanishOutboundCalls}
                            isActionCompleteHealthCareCard={true}
                            customActionLabels={['Confirmed', 'Left Message', 'Rescheduled', 'Cancelled', 'Total Success Rate']}
                            subValue={<><BoldValueSpan>{(hasSpanishOutboundCalls ? outboundConfirmedEnglish : outboundConfirmed ?? 0).toLocaleString()}</BoldValueSpan></>}
                            subValue2={<><BoldValueSpan>{(hasSpanishOutboundCalls ? outboundLeftMessageEnglish : outboundLeftMessage ?? 0).toLocaleString()}</BoldValueSpan></>}
                            subValue3={<><BoldValueSpan>{(hasSpanishOutboundCalls ? outboundRescheduledEnglish : outboundRescheduled ?? 0).toLocaleString()}</BoldValueSpan></>}
                            subValue4={<><BoldValueSpan>{(hasSpanishOutboundCalls ? outboundCancelledEnglish : outboundCancelled ?? 0).toLocaleString()}</BoldValueSpan></>}
                            subValue5={<><BoldValueSpan>{outboundSuccessRate}%</BoldValueSpan></>}
                            {...(hasSpanishOutboundCalls && {
                                subValueSpanish: <><BoldValueSpan>{outboundConfirmedSpanish.toLocaleString()}</BoldValueSpan></>,
                                subValue2Spanish: <><BoldValueSpan>{outboundLeftMessageSpanish.toLocaleString()}</BoldValueSpan></>,
                                subValue3Spanish: <><BoldValueSpan>{outboundRescheduledSpanish.toLocaleString()}</BoldValueSpan></>,
                                subValue4Spanish: <><BoldValueSpan>{outboundCancelledSpanish.toLocaleString()}</BoldValueSpan></>,
                            })}
                            icon={null}
                        />
                    ) : (
                        <StyledTopCard
                            title={appointmentsManagedTitleWithToggle}
                            value={getActionCompletedValue()}
                            deltaValue={null}
                            deltaPercentage={null}
                            showValue={false}
                            showPercentage={false}
                            hasSpanishCalls={hasSpanishCalls}
                            isActionCompleteHealthCareCard={healthcareApp}
                            subValue={getActionCompletedSubValues(1)}
                            subValue2={getActionCompletedSubValues(2)}
                            subValue3={getActionCompletedSubValues(3)}
                            subValue4={getActionCompletedSubValues(4)}
                            {...(hasSpanishCalls && {
                                subValueSpanish: getActionCompletedSubValuesSpanish(1),
                                subValue2Spanish: getActionCompletedSubValuesSpanish(2),
                                subValue3Spanish: getActionCompletedSubValuesSpanish(3),
                                subValue4Spanish: getActionCompletedSubValuesSpanish(4)
                            })}
                            icon={getIcon(actionCompletedTitle)}
                        />
                    )}
                    {foodOrderingApp &&
                        <StyledTopCard
                            title="Upsell Percentage"
                            value={upsellPercentage > -1 ? `${Math.round(upsellPercentage * 10) / 10}%` : "?"}
                            deltaValue={null}
                            deltaPercentage={null}
                            showValue={false}
                            tooltipMaxWidth={200}
                            subValue={getSubValue("Upsell Percentage")}
                            tooltip={getTooltip("Upsell Percentage")}
                            showPercentage={false}
                            icon={getIcon("Upsell Percentage")}
                        />
                    }
                    <StyledTopCard
                        title="Average Call Length"
                        value={formatHoursAndMinutes((averageCallLength / 60) || 0)}
                        showValue={props.showValue}
                        showPercentage={props.showPercentage}
                        subValue={getSubValue("Average Call Length")}
                        tooltip={getTooltip("Average Call Length")}
                        icon={callLengthImage}
                    />
                    {healthcareApp &&
                        <StyledTopCard
                            title="Contained Conversations"
                            value={`${containedCalls} (${containedCallsPercentage}%)`}
                            showValue={props.showValue}
                            showPercentage={props.showPercentage}
                            subValue={getTooltip("Call Containment")}
                            tooltip={""}
                            icon={containedCallsIcon}
                        />
                    }
                    {/* Outbound Confirmation Rate tile removed — success rate now shown inline in the Appointments Managed tile */}
                    {/* {isOutboundCallApp && healthcareApp &&
                        <StyledTopCard
                            title="Outbound Confirmation Rate"
                            value={`${outboundConfirmationRate}%`}
                            showValue={props.showValue}
                            showPercentage={props.showPercentage}
                            subValue={getTooltip("Outbound Confirmation Rate")}
                            tooltip={""}
                            icon={containedCallsIcon}
                        />
                    } */}
                    <StyledTopCard
                        title={hoursSavedTitle}
                        value={loadingTopLevelMetrics ? '...' : formattedHoursSaved}
                        showValue={false}
                        showPercentage={false}
                        subValue={getSubValue(hoursSavedTitle)}
                        icon={getIcon(hoursSavedTitle)}
                        tooltipMaxWidth={200}
                        tooltip={getTooltip(hoursSavedTitle)}
                    />
                    <StyledTopCard
                        title="Engagements"
                        value={formatEngagementsWithCalls(uniqueEngagementCount)}
                        deltaValue={null}
                        deltaPercentage={null}
                        showValue={false}
                        showPercentage={false}
                        icon={getIcon("Engagements")}
                        tooltipMaxWidth={200}
                        tooltip={getTooltip("Engagements")}
                        subValue={getSubValue("Engagements")}
                    />
                </TopCardsContainer>
                {isOutboundCallApp && healthcareApp && props.organizationId && (
                    <AppointmentSummaryCard>
                        <AppointmentSummaryHeader>
                            <AppointmentSummaryLabel>Appointment Summary</AppointmentSummaryLabel>
                            {!isLoadingAppointmentSummary && appointmentResponseRate !== null && (
                                <AppointmentResponseRateBadge>
                                    <TooltipWrapper text="Percentage of appointments where a final outcome (confirmed, rescheduled, cancelled, or checked out) was recorded, out of all appointments excluding those where only a voicemail was left with no further response." place="top" maxWidth={280}>
                                        <span>{appointmentResponseRate}% Response Rate</span>
                                    </TooltipWrapper>
                                </AppointmentResponseRateBadge>
                            )}
                            {isLoadingAppointmentSummary && (
                                <AppointmentResponseRateBadge>
                                    <span>... Response Rate</span>
                                </AppointmentResponseRateBadge>
                            )}
                        </AppointmentSummaryHeader>
                        <AppointmentSummaryGrid>
                            <AppointmentMetric>
                                <MetricLabel>Total Appointments</MetricLabel>
                                <MetricValue>{isLoadingAppointmentSummary ? "..." : appointmentSummaryFetchOk ? totalAppointments.toLocaleString() : "--"}</MetricValue>
                            </AppointmentMetric>
                            <AppointmentMetric>
                                <MetricLabelRow>
                                    <MetricLabel>Total Confirmed</MetricLabel>
                                    <TooltipWrapper text="Final recorded status for appointments Voicify called. Includes confirmations from any channel — e.g., if Voicify left a message and the patient called back to confirm, it counts here." place="top" maxWidth={280}>
                                        <InfoIcon>i</InfoIcon>
                                    </TooltipWrapper>
                                </MetricLabelRow>
                                <MetricValue>{isLoadingAppointmentSummary ? "..." : appointmentSummaryFetchOk ? totalConfirmed.toLocaleString() : "--"}</MetricValue>
                            </AppointmentMetric>
                            <AppointmentMetric>
                                <MetricLabelRow>
                                    <MetricLabel>Total Cancelled</MetricLabel>
                                    <TooltipWrapper text="Final recorded status for appointments Voicify called. Includes cancellations from any channel — e.g., if Voicify left a message and the patient called back to cancel, it counts here." place="top" maxWidth={280}>
                                        <InfoIcon>i</InfoIcon>
                                    </TooltipWrapper>
                                </MetricLabelRow>
                                <MetricValue>{isLoadingAppointmentSummary ? "..." : appointmentSummaryFetchOk ? totalCancelled.toLocaleString() : "--"}</MetricValue>
                            </AppointmentMetric>
                            <AppointmentMetric>
                                <MetricLabelRow>
                                    <MetricLabel>Total Rescheduled</MetricLabel>
                                    <TooltipWrapper text="Final recorded status for appointments Voicify called. Includes reschedules from any channel — e.g., if Voicify left a message and the patient called back to reschedule, it counts here." place="top" maxWidth={280}>
                                        <InfoIcon>i</InfoIcon>
                                    </TooltipWrapper>
                                </MetricLabelRow>
                                <MetricValue>{isLoadingAppointmentSummary ? "..." : appointmentSummaryFetchOk ? totalReschedules.toLocaleString() : "--"}</MetricValue>
                            </AppointmentMetric>
                            <AppointmentMetric>
                                <MetricLabelRow>
                                    <MetricLabel>Total Checked Out</MetricLabel>
                                    <TooltipWrapper text="Final recorded status for appointments Voicify called. Includes check-outs from any channel — e.g., if Voicify left a message and the patient later checked out, it counts here." place="top" maxWidth={280}>
                                        <InfoIcon>i</InfoIcon>
                                    </TooltipWrapper>
                                </MetricLabelRow>
                                <MetricValue>{isLoadingAppointmentSummary ? "..." : appointmentSummaryFetchOk ? totalCheckedOut.toLocaleString() : "--"}</MetricValue>
                            </AppointmentMetric>
                            <AppointmentMetric>
                                <MetricLabelRow>
                                    <MetricLabel>Total Left Message</MetricLabel>
                                    <TooltipWrapper text="Appointments Voicify called outbound where the final recorded status is a voicemail with no further response. If the patient called back and took an action (e.g., confirmed or cancelled), that appointment moves to the corresponding category instead." place="top" maxWidth={280}>
                                        <InfoIcon>i</InfoIcon>
                                    </TooltipWrapper>
                                </MetricLabelRow>
                                <MetricValue>{isLoadingAppointmentSummary ? "..." : appointmentSummaryFetchOk ? totalLeftMessage.toLocaleString() : "--"}</MetricValue>
                            </AppointmentMetric>
                            <AppointmentMetric>
                                <MetricLabelRow>
                                    <MetricLabel>Future - Confirmed</MetricLabel>
                                    <TooltipWrapper text="Shows upcoming appointments confirmed by Voicify before the appointment date. Once an appointment date passes, it is no longer included here — this reflects live confirmations for future appointments only." place="top" maxWidth={260}>
                                        <InfoIcon>i</InfoIcon>
                                    </TooltipWrapper>
                                </MetricLabelRow>
                                <MetricValue>{isLoadingAppointmentSummary ? "..." : appointmentSummaryFetchOk ? confirmedByVoicify.toLocaleString() : "--"}</MetricValue>
                            </AppointmentMetric>
                            <AppointmentMetric>
                                <MetricLabelRow>
                                    <MetricLabel>Future - Left Message</MetricLabel>
                                    <TooltipWrapper text="Appointments where Voicify left a message before the appointment date. Once an appointment date passes, it is no longer included here." place="top" maxWidth={260}>
                                        <InfoIcon>i</InfoIcon>
                                    </TooltipWrapper>
                                </MetricLabelRow>
                                <MetricValue>{isLoadingAppointmentSummary ? "..." : appointmentSummaryFetchOk ? leftMessageByVoicify.toLocaleString() : "--"}</MetricValue>
                            </AppointmentMetric>
                        </AppointmentSummaryGrid>
                    </AppointmentSummaryCard>
                )}
                <BottomSection>
                    <LeftCardsContainer>
                        <StyledLeftCard
                            title={healthcareApp ? "Repeat Patients" : "Repeat Customers"}
                            tooltipMaxWidth={200}
                            tooltip={getTooltip("Repeat Customers")}
                            subValue={getSubValue("Repeat Customers")}
                            value={repeatCustomers > -1 ? repeatCustomers : "?"}
                            deltaValue={null}
                            deltaPercentage={null}
                            showValue={false}
                            showPercentage={false}
                            icon={getIcon("Repeat Customers")}
                        />
                        <StyledBottomLeftCard
                            title={healthcareApp ? "Converted Patients" : "Converted Customers"}
                            tooltipMaxWidth={200}
                            tooltip={getTooltip("Converted Customers")}
                            value={convertedCustomers > -1 ? convertedCustomers : "?"}
                            subValue={getSubValue("Converted Customers")}
                            deltaValue={null}
                            deltaPercentage={null}
                            showValue={false}
                            showPercentage={false}
                            icon={getIcon("Converted Customers")}
                        />
                    </LeftCardsContainer>
                    <ChartContainer>
                        <StyledGraphDiv>
                            {chart}
                        </StyledGraphDiv>
                    </ChartContainer>
                </BottomSection>
            </StyledTopMetricsContainer>
        );
    };


    return (
        <div className={wrapperStyle}>
            {everythingIsLoading() || (!sparkContainer.inSpark && !props.data) ? <div className="loader-container"><Loader /></div> :
                <>
                    {!sparkContainer.inSpark && <h3>App Summary</h3>}
                    <TotalsWrapper>
                        {sparkContainer.inSpark ?
                            ((foodOrderingApp || healthcareApp) && useGenerativeReview) ?
                                renderGenerativeReviewCards()
                                :
                                <>
                                    <StyledTotalCard
                                        title="Calls"
                                        value={props.totals?.totalSessions}
                                        deltaValue={deltaSessions}
                                        deltaPercentage={Math.round(deltaSessions / (props.previousTotals?.totalSessions > 0 ? props.previousTotals?.totalSessions : 1) * 100)}
                                        showValue={props.showValue}
                                        showPercentage={props.showPercentage}
                                        icon={phoneIconBlue}
                                    />
                                    <StyledTotalCard
                                        title="Average Call Length"
                                        value={formatNumber(averageCallLength || 0)}
                                        deltaValue={parseFloat(formatNumber(deltaAverageCallLength || 0))}
                                        deltaPercentage={Math.round(deltaAverageCallLength / (previousAverageCallLength > 0 ? previousAverageCallLength : 1) * 100)}
                                        showValue={props.showValue}
                                        showPercentage={props.showPercentage}
                                        icon={callLengthImage}
                                    />
                                    <StyledTotalCard
                                        title="Hours Saved"
                                        value={Math.round(((props.totals?.totalMinutes ?? 0) / 60) * 100) / 100}
                                        showValue={false}
                                        showPercentage={false}
                                        icon={laborHoursSavedIcon}
                                    />
                                    <StyledTotalCard
                                        title="Unique Callers"
                                        value={props.totals?.totalUsers}
                                        deltaValue={Math.round(deltaNumberOfCaller)}
                                        deltaPercentage={Math.round(deltaNumberOfCaller / (props.previousTotals?.totalUsers > 0 ? props.previousTotals?.totalUsers : 1) * 100)}
                                        showValue={props.showValue}
                                        showPercentage={props.showPercentage}
                                        icon={uniqueCallersImage}
                                    />
                                    <StyledTotalCard
                                        title="Average Calls per Day"
                                        value={averageCallsPerDay}
                                        deltaValue={deltaAverageCallsPerDay}
                                        deltaPercentage={Math.round(deltaAverageCallsPerDay / (previousAverageCallsPerDay > 0 ? previousAverageCallsPerDay : 1) * 100)}
                                        showValue={props.showValue}
                                        showPercentage={props.showPercentage}
                                        icon={averageIconBlue}
                                        secondIcon={phoneIconBlue}
                                    />
                                    {!useGenerativeReview ?
                                        <StyledTotalCard
                                            title="Total Topic Inquiries"
                                            value={props.filteredInteractionsCount}
                                            deltaValue={deltaInteractions}
                                            deltaPercentage={Math.round(deltaInteractions / props.filteredInteractionsCount * 100)}
                                            showValue={props.showValue}
                                            showPercentage={props.showPercentage}
                                            icon={questionIconBlue}
                                        /> : <></>}
                                    {!useGenerativeReview ? <StyledTotalCard
                                        title="Average Topic Inquiries per Call"
                                        value={Math.round(Number(averageTopicInquiriesPerCall.toFixed(2)))}
                                        deltaValue={Math.round(Number(deltaAverageTopicInquiriesPerCall.toFixed(2)))}
                                        deltaPercentage={deltaAverageTopicInquiriesPerCallPercentage}
                                        showValue={props.showValue}
                                        showPercentage={props.showPercentage}
                                        icon={averageIconBlue}
                                        secondIcon={questionIconBlue}
                                    /> : <></>}

                                </>
                            :
                            <>
                                <TotalCard
                                    title="Users"
                                    value={props.totals?.totalUsers}
                                    deltaValue={deltaUsers}
                                    deltaPercentage={Math.round(deltaUsers / (props.previousTotals?.totalUsers > 0 ? props.previousTotals?.totalUsers : 1) * 100)}
                                    showValue={props.showValue}
                                    showPercentage={props.showPercentage}
                                />
                                <TotalCard
                                    title="New Users"
                                    value={props.totals?.totalNewUsers ?? 0}
                                    deltaValue={deltaNewUsers}
                                    deltaPercentage={Math.round(deltaNewUsers / (props.previousTotals?.totalNewUsers > 0 ? props.previousTotals?.totalNewUsers : 1) * 100)}
                                    showValue={props.showValue}
                                    showPercentage={props.showPercentage}
                                />
                                <TotalCard
                                    title="Sessions"
                                    value={props.totals?.totalSessions}
                                    deltaValue={deltaSessions}
                                    deltaPercentage={Math.round(deltaSessions / (props.previousTotals?.totalSessions > 0 ? props.previousTotals?.totalSessions : 1) * 100)}
                                    showValue={props.showValue}
                                    showPercentage={props.showPercentage}
                                />
                                <TotalCard
                                    title="Interactions"
                                    value={props.totals?.totalRequests}
                                    deltaValue={deltaRequests}
                                    deltaPercentage={Math.round(deltaRequests / (props.previousTotals?.totalRequests > 0 ? props.previousTotals?.totalRequests : 1) * 100)}
                                    showValue={props.showValue}
                                    showPercentage={props.showPercentage}
                                />
                            </>
                        }
                    </TotalsWrapper>
                    {!sparkContainer.inSpark &&
                        <h3 className="ac-analytics-hits-table">By Content</h3>
                    }
                    {renderTable()}
                    {displayGraphModal &&
                        <GraphFilterModal
                            isVisible={displayGraphModal}
                            onClose={() => setDisplayGraphModal(false)}
                            categories={ALL_CATEGORIES}
                            categoriesForDisplay={ALL_CATEGORIES.map(cat => getCategoryName(cat, assistantIndustry))}
                            tags={allTags}
                            tagsForDisplay={allTags.map(tag => getTagName(tag, assistantIndustry))}
                            onToggleTag={(tag) => toggleGraphTag(tag)}
                            onToggleCategory={(cat) => toggleGraphCategories(cat)}
                            onApply={async () => {
                                setGraphCategories([...toggledGraphCategories])
                                setGraphTags([...toggledGraphTags])
                            }}
                            assistantIndustry={assistantIndustry}
                            currentToggledCategories={[...toggledGraphCategories]}
                            currentToggledTags={[...toggledGraphTags]}
                        />}
                    {showActionCompletedHistogram &&
                        <SmallModalLayout
                            isVisible={showActionCompletedHistogram}
                            onClose={() => setShowActionCompletedHistogram(false)}
                            className={modalContentStyle}
                            innerClassName={modalInnerStyle}
                        >
                            <ModalHeader title={foodOrderingApp ? "Order Placed Volume" : healthcareApp ? "Appointment Booking Volume" : "Completed Action Volume"} onClose={() => setShowActionCompletedHistogram(false)} />
                            {actionCompletedHistogram}
                        </SmallModalLayout>
                    };
                </>}
        </div>
    );
}

const StyledTotalCard = styled(TotalCard)`
    padding: 24px 32px;
    height: 200px;
    width: 100%;
    text-align: center;
    font-family: Muli;
    border: 1px solid #CBCCD2;
    box-sizing: border-box;
    border-radius: 8px;
    background: white;
    display: flex;
    flex-direction: column;
    justify-content: center;
    
    @media (max-width: 1200px) {
        height: auto;
        min-height: 150px;
    }
`;

const BoldValueSpan = styled.span`
    color: ${color_text_link};
    font-weight: bold;
`;

const wrapperStyle = css`
margin: 0;
.loader-container {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100vh;
    .spinner > div {
        background: ${color_colors_ocean};
    }
}
>h3 {
    margin: 16px 32px;
}
`

const TotalsWrapper = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    margin: 0 16px;
    padding-bottom: 16px;
`;

const containerStyle = css`
    .feature-type-icon {
        margin-right: 12px;
        width: 24px;
        height: 24px;
    }
    a {
        color: ${color_text_default};
        &:hover {
            color: ${color_text_link};
        }
    }
`

const GraphButtonDiv = styled.div`
    justify-content: center;
    margin: 0
    align-items: center;
`;


const StyledLink = styled.a`
     && {
        color: ${color_text_link};
    }
`;


const GraphButton = styled(Button)`
    font-size: 11px;
    width: 80px;
    height: 23px;
    margin-bottom: 0;
    margin-top: 0;
`;

// Styled components for the three-row grid layout

const StyledTopMetricsContainer = styled.div`
    --card-gap: 24px;
    --responsive-gap-medium: 16px;
    --responsive-gap-small: 12px;
    --vertical-card-gap: 16px;
    --graph-left-adjust: 0px;
    --bottom-component-vertical-adjust: 16px;
    --card-height: 200px;
        
    display: flex;
    flex-direction: column;
    padding: var(--card-gap);
    width: 100%;
    max-width: 100vw;
    box-sizing: border-box;
    margin-bottom: 40px;
    
    @media (max-width: 768px) {
        padding: var(--responsive-gap-medium);
        gap: var(--responsive-gap-medium);
    }
    
`;

const BottomSection = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  grid-template-rows: var(--card-height) var(--card-height);
  gap: var(--card-gap);
  width: 100%;
  margin-top: var(--card-gap);

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }

  /* debug
  border: 1px solid blue;
  */
`;

const LeftCardsContainer = styled.div`
  grid-column: 1 / 2;
  grid-row: 1 / 3;

  display: grid;
  grid-template-rows: 1fr 1fr;     /* two equal “card rows” */
  gap: var(--vertical-card-gap);
  position: relative;
  left: -16px;

  @media (max-width: 1200px) {
    grid-column: 1;
    grid-row: auto;
    grid-template-rows: auto;
  }
`;

const ChartContainer = styled.div`
  grid-column: 2 / 6;   /* columns 2,3,4 */
  grid-row: 1 / 3;      /* spans two rows */

  display: grid;        /* lets the child stretch */

  /* remove any stray margins from inner card */
  > * {
    width: 100%;
    height: 100%;
    margin: 0;
  }

  @media (max-width: 1200px) {
    grid-column: 1;
    grid-row: auto;
    min-height: auto;
  }

  /* Debug
  border: 1px solid red;
  */
`;

const TopCardsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: var(--card-gap);
    width: 100%;
    position: relative;
    left: -16px;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
        gap: var(--responsive-gap-medium);
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: var(--responsive-gap-small);
    }
    
    /* debug
    border: 1px solid red;
    */
`;

const histogramTitleStyle = css`
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${color_text_light};
    margin: 0;
    text-align: center;
`;

const StyledTopCard = styled(TotalCard)`
    padding: 24px;
    height: 200px;
    width: 100%;
    text-align: center;
    font-family: Muli;
    border: 1px solid #CBCCD2;
    box-sizing: border-box;
    border-radius: 8px;
    background: white;
    display: flex;
    flex-direction: column;
    justify-content: center;
    
    @media (max-width: 1200px) {
        height: auto;
        min-height: 150px;
    }
`;

const StyledLeftCard = styled(TotalCard)`
    padding: 24px 32px;
    height: var(--card-height);
    width: 100%;
    text-align: center;
    font-family: Muli;
    border: 1px solid #CBCCD2;
    box-sizing: border-box;
    border-radius: 8px;
    background: white;
    display: flex;
    flex-direction: column;
    justify-content: center;
    
    @media (max-width: 1200px) {
        height: auto;
        min-height: 200px;
    }
`;


const AppointmentSummaryCard = styled.div`
    padding: 16px 24px;
    height: auto;
    font-family: Muli;
    border: 1px solid #CBCCD2;
    box-sizing: border-box;
    border-radius: 8px;
    background: white;
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    width: 100%;
    margin-top: var(--card-gap);
    box-shadow: 0px 2px 4px rgba(50, 70, 97, 0.1);

    @media (max-width: 1200px) {
        left: 0;
        width: 100%;
        min-height: 120px;
    }
`;

const AppointmentSummaryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 8px;
    flex-wrap: wrap;
`;

const AppointmentResponseRateBadge = styled.div`
    display: inline-flex;
    align-items: center;
    background: #e8f4fb;
    border: 1px solid #007dbb;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 600;
    color: #007dbb;
    cursor: default;
    white-space: nowrap;
`;

const AppointmentSummaryLabel = styled.p`
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${color_text_light};
    margin: 0;
    text-align: center;
`;

const AppointmentSummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 8px;
    flex: 1;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const AppointmentMetric = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 4px;
    border-radius: 4px;
    cursor: default;
`;

const MetricLabel = styled.p`
    font-size: 11px;
    line-height: 13px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${color_text_light};
    margin: 0 0 4px 0;
    text-align: center;
`;

const MetricValue = styled.p`
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    text-align: center;
`;

const MetricLabelRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-bottom: 4px;
    ${MetricLabel} {
        margin-bottom: 0;
    }
`;

const InfoIcon = styled.span`
    width: 14px;
    height: 14px;
    border: 1px solid #007dbb;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: sans-serif;
    font-size: 10px;
    font-style: italic;
    color: #007dbb;
    cursor: pointer;
    flex-shrink: 0;
    line-height: 1;
`;

const StyledBottomLeftCard = styled(TotalCard)`
    padding: 24px 32px;
    height: var(--card-height);
    width: 100%;
    text-align: center;
    font-family: Muli;
    border: 1px solid #CBCCD2;
    box-sizing: border-box;
    border-radius: 8px;
    background: white;
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    bottom: var(--bottom-component-vertical-adjust);

    
    @media (max-width: 1200px) {
        height: auto;
        min-height: 200px;
    }
    
`;

const StyledGraphDiv = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    margin: 16px 0;
    position: relative;
    left: var(--graph-left-adjust);
    top: 0px;
    
    > div {
        flex: 1;
        margin: 0;
        padding: 0;
        box-sizing: border-box;
        
        /* Ensure the HistogramChartCard fits exactly */
        height: 432px;
        min-height: 432px;
        max-height: 432px;
    }
    /* debug
    border: 1px solid blue;
    */
`;



const modalContentStyle = css`
    width: 100%;
    max-width: 900px;
    max-height: 80vh;

    @media (max-width: 768px) {
        width: 98%;
        max-width: 100%;
        max-height: 90vh;
    }
`;

const modalInnerStyle = css`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const InlineViewToggle = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    background: ${color_gradients_green_shift};
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 9px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, opacity 0.1s ease;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    line-height: 1.4;
    vertical-align: middle;

    &:hover {
        opacity: 0.85;
    }
`;

export default ApplicationTopHitsTable;
